"use client";

import { Fragment, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { formatISTDateTime, maskPhone } from "../../helper";
import FloatIssueForm from "./floatIssueForm.js";
import { FloatChipTable, calcAmount, loadRegularChips } from "./floatChipTable.js";

const fmt = (v) => `${Number(v || 0).toLocaleString("en-IN")}`;
const chipSummary = (chips = []) => chips.map((c) => `${c.quantity}×${c.denomination}`).join(", ") || "—";

const SOURCE_LABEL = {
  CASHOUT:     "Chip Cash-Out",
  CHIP_RETURN: "Chips Returned",
  DEPOSIT:     "Deposit",
};

const failed = (res) => res?.status === false || res?.data?.status === false || res?.status === "fail";
const failMsg = (res, fallback) => res?.message || res?.data?.message || fallback;

/* ─── Return chips against float ─── */
function ReturnChipsDialog({ float, onClose, onDone, token }) {
  const [chips, setChips]     = useState([]);
  const [qty, setQty]         = useState({});
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (!float) return;
    setQty({}); setRemarks(""); setError("");
    loadRegularChips(token).then(setChips);
  }, [float]);

  const total = chips.reduce((s, c) => s + calcAmount(c.denomination, qty[c.denomination]), 0);
  const over  = float && total > float.outstanding;

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const res = await axiosRequest("POST", `${apiBaseUrl}/float-exchange/${float._id}/return-chips`, token, {
        chips: chips
          .filter((c) => Number(qty[c.denomination]) > 0)
          .map((c) => ({ denomination: c.denomination, quantity: Number(qty[c.denomination]) })),
        remarks,
        date: new Date().toISOString(),
      });
      if (failed(res)) { setError(failMsg(res, "Failed to return chips.")); return; }
      onDone();
    } catch { setError("Something went wrong."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!float} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Return Chips — {float?.user_name || float?.player_id}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Outstanding: <b>{fmt(float?.outstanding)}</b>. Chips go back to the counter and reduce the float.
        </Typography>
        <FloatChipTable chips={chips} qty={qty} setQty={setQty} qtyLabel="Qty Returned" />
        <Typography fontWeight={700} mb={2} color={over ? "error" : "text.primary"}>
          Returning: {fmt(total)}{over ? " — exceeds outstanding" : ""}
        </Typography>
        <TextField fullWidth size="small" label="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || total <= 0 || over}>
          {saving ? "Saving..." : "Return Chips"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── Settle float with cash / bank / other ─── */
function SettleCashDialog({ float, onClose, onDone, token }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode]     = useState("cash");
  const [utr, setUtr]       = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (!float) return;
    setAmount(String(float.outstanding || "")); setMode("cash"); setUtr(""); setRemark(""); setError("");
  }, [float]);

  const amt  = Number(amount);
  const over = float && amt > float.outstanding;

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const res = await axiosRequest("POST", `${apiBaseUrl}/float-exchange/${float._id}/settle-cash`, token, {
        amount: amt, depositType: mode, utr: mode === "bank" ? utr : "", remark,
      });
      if (failed(res)) { setError(failMsg(res, "Failed to settle float.")); return; }
      onDone();
    } catch { setError("Something went wrong."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!float} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Settle Float — {float?.user_name || float?.player_id}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Outstanding: <b>{fmt(float?.outstanding)}</b>. Recorded as a deposit{mode === "other" ? "" : ` — counter ${mode} goes up`}, wallet is not credited.
        </Typography>
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)} sx={{ mb: 2 }}>
          <ToggleButton value="cash">Cash</ToggleButton>
          <ToggleButton value="bank">Bank</ToggleButton>
          <ToggleButton value="other">Other</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          fullWidth size="small" type="number" label="Amount" sx={{ mb: 2 }}
          value={amount} onChange={(e) => setAmount(e.target.value)}
          error={over} helperText={over ? "Exceeds outstanding" : ""}
        />
        {mode === "bank" && (
          <TextField fullWidth size="small" label="UTR (optional)" sx={{ mb: 2 }} value={utr} onChange={(e) => setUtr(e.target.value)} />
        )}
        <TextField fullWidth size="small" label="Remark (optional)" value={remark} onChange={(e) => setRemark(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={submit} disabled={saving || !(amt > 0) || over}>
          {saving ? "Saving..." : "Settle"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── One float row + expandable repayment history ─── */
function FloatRow({ rec, onReturn, onSettle }) {
  const [open, setOpen] = useState(false);
  const isOpen = rec.status === "OPEN";
  return (
    <Fragment>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setOpen(!open)} disabled={!rec.repayments?.length}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{formatISTDateTime(rec.date)}</TableCell>
        <TableCell>
          <Typography fontWeight={600}>{rec.user_name || "—"}</Typography>
          <Typography variant="caption" color="text.secondary">{rec.player_id} · {maskPhone(rec.phone) || "—"}</Typography>
          {rec.reference_name && (
            <Typography variant="caption" display="block" color="text.secondary">Ref: {rec.reference_name}</Typography>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{chipSummary(rec.chips)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(rec.total_amount)}</TableCell>
        <TableCell align="right" sx={{ color: "#66bb6a" }}>{fmt(rec.repaid_amount)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, color: rec.outstanding > 0 ? "#ef5350" : "text.secondary" }}>{fmt(rec.outstanding)}</TableCell>
        <TableCell>
          <Chip size="small" label={rec.status} color={isOpen ? "warning" : "default"} />
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          {isOpen && (
            <>
              <Button size="small" onClick={() => onReturn(rec)}>Return Chips</Button>
              <Button size="small" color="success" onClick={() => onSettle(rec)}>Settle Amount</Button>
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box p={2} sx={{ background: "#1c2433" }}>
              <Typography variant="body2" fontWeight={700} mb={1}>Repayments</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rec.repayments || []).map((p, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 12 }}>{formatISTDateTime(p.date)}</TableCell>
                      <TableCell>{SOURCE_LABEL[p.source] || p.source}</TableCell>
                      <TableCell>{p.payment_mode || (p.source === "DEPOSIT" ? "—" : "CHIPS")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#66bb6a" }}>{fmt(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

const FloatTable = ({ rows, empty, onReturn, onSettle }) => (
  <Paper sx={{ borderRadius: 4, overflow: "auto", mb: 4 }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell>Date</TableCell>
          <TableCell>Player</TableCell>
          <TableCell>Chips</TableCell>
          <TableCell align="right">Float</TableCell>
          <TableCell align="right">Repaid</TableCell>
          <TableCell align="right">Outstanding</TableCell>
          <TableCell>Status</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length > 0
          ? rows.map((rec) => <FloatRow key={rec._id} rec={rec} onReturn={onReturn} onSettle={onSettle} />)
          : <TableRow><TableCell colSpan={9} align="center">{empty}</TableCell></TableRow>}
      </TableBody>
    </Table>
  </Paper>
);

/* ─── Per-player totals across all floats ─── */
const PlayerTotalsTable = ({ rows }) => (
  <Paper sx={{ borderRadius: 4, overflow: "auto", mb: 4 }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="right">Floats</TableCell>
          <TableCell align="right">Total Float</TableCell>
          <TableCell align="right">Repaid</TableCell>
          <TableCell align="right">Outstanding</TableCell>
          <TableCell>Last Issued</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length > 0
          ? rows.map((p) => (
              <TableRow hover key={p._id}>
                <TableCell>
                  <Typography fontWeight={600}>{p.user_name || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.player_id} · {maskPhone(p.phone) || "—"}</Typography>
                </TableCell>
                <TableCell align="right">
                  {p.floats}
                  {p.open_floats > 0 && (
                    <Typography variant="caption" display="block" color="text.secondary">{p.open_floats} open</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(p.total)}</TableCell>
                <TableCell align="right" sx={{ color: "#388e3c" }}>{fmt(p.repaid)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: p.outstanding > 0 ? "#d32f2f" : "text.secondary" }}>{fmt(p.outstanding)}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{formatISTDateTime(p.last_issued)}</TableCell>
              </TableRow>
            ))
          : <TableRow><TableCell colSpan={6} align="center">No floats yet</TableCell></TableRow>}
      </TableBody>
    </Table>
  </Paper>
);

export default function FloatExchange() {
  const token = useSelector((state) => state.loginSlice.token);

  const [playerTotals, setPlayerTotals] = useState([]);
  const [openFloats, setOpenFloats]     = useState([]);
  const [closedFloats, setClosedFloats] = useState([]);
  const [outstanding, setOutstanding]   = useState({ total: 0, count: 0 });
  const [loading, setLoading]           = useState(false);
  const [issueOpen, setIssueOpen]       = useState(false);
  const [returnFor, setReturnFor]       = useState(null);
  const [settleFor, setSettleFor]       = useState(null);

  // All open floats are listed (cashier needs every one to settle it);
  // closed history follows the last-3 cap used on other activity pages.
  const fetchFloats = async () => {
    setLoading(true);
    try {
      const [openRes, closedRes] = await Promise.all([
        axiosRequest("GET", `${apiBaseUrl}/float-exchange?status=OPEN&page=1&size=200&player_totals=true`, token),
        axiosRequest("GET", `${apiBaseUrl}/float-exchange?status=CLOSED&page=1&size=3`, token),
      ]);
      setOpenFloats(openRes?.data?.floatList || []);
      setPlayerTotals(openRes?.data?.playerTotals || []);
      setOutstanding(openRes?.data?.openOutstanding || { total: 0, count: 0 });
      setClosedFloats(closedRes?.data?.floatList || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFloats(); }, []);

  const done = () => { setReturnFor(null); setSettleFor(null); fetchFloats(); };

  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" } }}>
      <Box p={3} sx={{ padding: { xs: "16px", md: "32px" } }}>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Float Exchange</Typography>
            <Typography color="text.secondary">
              One-time chip advance — repaid first on chip cash-out or deposit, then loan, then wallet
            </Typography>
          </Box>
          <Button
            variant="contained" color="warning"
            startIcon={<AddIcon />}
            onClick={() => setIssueOpen(true)}
            sx={{ borderRadius: 3, height: 45 }}
          >
            Issue Float
          </Button>
        </Box>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, background: "#2e2008", borderRadius: 2, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">Float Outstanding</Typography>
              <Typography variant="h5" fontWeight={700} color="#ffb74d" mt={0.5}>{fmt(outstanding.total)}</Typography>
              <Typography variant="caption" color="text.secondary">{outstanding.count} open</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight={600} mb={1}>Per Player Total</Typography>
        <PlayerTotalsTable rows={playerTotals} />

        <Typography variant="h6" fontWeight={600} mb={1}>Open Floats</Typography>
        {loading && !openFloats.length
          ? <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
          : <FloatTable rows={openFloats} empty="No open floats" onReturn={setReturnFor} onSettle={setSettleFor} />}

        <Typography variant="h6" fontWeight={600} mb={1}>Recently Settled</Typography>
        <FloatTable rows={closedFloats} empty="No settled floats yet" onReturn={setReturnFor} onSettle={setSettleFor} />

        <FloatIssueForm open={issueOpen} onClose={() => setIssueOpen(false)} refresh={fetchFloats} />
        <ReturnChipsDialog float={returnFor} onClose={() => setReturnFor(null)} onDone={done} token={token} />
        <SettleCashDialog float={settleFor} onClose={() => setSettleFor(null)} onDone={done} token={token} />
      </Box>
    </Box>
  );
}
